import React, { useEffect, useState } from "react";
import { Hash, Loader2, Wallet, Download, TrendingUp, TrendingDown, Minus, Filter } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const INTENT_COLOR = {
  informational: "bg-sky-500/15 text-sky-300",
  navigational: "bg-violet-500/15 text-violet-300",
  transactional: "bg-emerald-500/15 text-emerald-300",
  commercial: "bg-amber-500/15 text-amber-300",
};
const TREND_ICON = { rising: TrendingUp, declining: TrendingDown, stable: Minus };
const TREND_COLOR = { rising: "text-emerald-400", declining: "text-rose-400", stable: "text-slate-400" };

function diffColor(d) {
  if (d <= 20) return "bg-emerald-500";
  if (d <= 50) return "bg-amber-500";
  if (d <= 75) return "bg-orange-500";
  return "bg-rose-500";
}

export default function KeywordResearch() {
  const [seed, setSeed] = useState("");
  const [country, setCountry] = useState("US");
  const [count, setCount] = useState(20);
  const [busy, setBusy] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [provider, setProvider] = useState("");
  const [wallet, setWallet] = useState(null);
  const [filter, setFilter] = useState("all");

  const COST = 3;

  const loadWallet = async () => {
    try { setWallet((await api.get("/credits/me")).data); } catch { /* silent */ }
  };
  useEffect(() => { loadWallet(); }, []);

  const run = async () => {
    if (seed.trim().length < 2) { toast.error("Enter a seed keyword (2+ chars)"); return; }
    if (wallet && wallet.balance < COST) { toast.error("Not enough credits"); return; }
    setBusy(true); setKeywords([]);
    try {
      const { data } = await api.post("/seo/keywords", {
        seed: seed.trim(), country, language: "en", count: Number(count),
      });
      setKeywords(data.result?.keywords || []);
      setProvider(data.result?.provider || data.provider || "heuristic");
      toast.success(`Generated ${data.result?.keywords?.length || 0} keywords`);
      loadWallet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Research failed");
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    if (!keywords.length) return;
    const rows = [
      ["keyword","monthly_volume","difficulty","cpc_usd","intent","trend"],
      ...keywords.map(k => [k.keyword, k.monthly_volume, k.difficulty, k.cpc_usd, k.intent, k.trend]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `keywords-${seed.replace(/\s+/g,'_')}.csv`;
    a.click();
  };

  const filtered = filter === "all" ? keywords : keywords.filter(k => k.intent === filter);

  return (
    <div className="space-y-6" data-testid="seo-keywords-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3" style={{fontFamily:'Outfit'}}>
            <Hash className="text-blue-400" /> Keyword Research
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Discover keywords with volume, difficulty, intent & CPC — {COST} credits / run.</p>
        </div>
        <Link to="/dashboard/credits" className="card px-4 py-2.5 flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="seo-credits-balance">
          <Wallet size={15} className="text-blue-400" />
          <span className="font-semibold text-white">{wallet?.balance ?? "—"}</span> credits
        </Link>
      </header>

      <div className="card p-5 flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs text-slate-400 block mb-1">Seed keyword</label>
          <input
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && run()}
            placeholder="e.g. ai website builder"
            className="w-full bg-slate-950/80 border border-slate-700 focus:border-blue-500 outline-none rounded-lg px-4 py-2.5 text-white"
            data-testid="seo-keywords-seed-input"
          />
        </div>
        <div className="w-28">
          <label className="text-xs text-slate-400 block mb-1">Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-slate-950/80 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm">
            {["US","IN","GB","CA","AU","DE","FR","BR","JP","SG"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="w-28">
          <label className="text-xs text-slate-400 block mb-1">Count</label>
          <select value={count} onChange={(e) => setCount(e.target.value)}
                  className="w-full bg-slate-950/80 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm">
            {[10, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button onClick={run} disabled={busy} className="btn-primary" data-testid="seo-keywords-run-btn">
          {busy ? <><Loader2 className="animate-spin" size={16}/> Generating…</> :
                   <><Hash size={16}/> Research · {COST} cr</>}
        </button>
      </div>

      {keywords.length > 0 && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-slate-300 text-sm">
              <Filter size={14}/> Intent:
            </div>
            {["all","informational","navigational","transactional","commercial"].map(v => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={`px-3 py-1.5 rounded-full text-xs ${filter === v ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                data-testid={`seo-intent-filter-${v}`}
              >
                {v}
              </button>
            ))}
            <div className="flex-1"/>
            <span className="text-xs text-slate-500">via <span className="text-slate-300 font-semibold">{provider}</span></span>
            <button onClick={exportCsv} className="card px-3 py-1.5 text-xs text-slate-300 hover:text-white flex items-center gap-1.5" data-testid="seo-keywords-export-btn">
              <Download size={13}/> Export CSV
            </button>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full text-sm" data-testid="seo-keywords-table">
              <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Keyword</th>
                  <th className="text-right p-3">Volume / mo</th>
                  <th className="text-left p-3 w-40">Difficulty</th>
                  <th className="text-right p-3">CPC (USD)</th>
                  <th className="text-left p-3">Intent</th>
                  <th className="text-center p-3">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.map((k, i) => {
                  const TI = TREND_ICON[k.trend] || Minus;
                  return (
                    <tr key={i} className="hover:bg-slate-900/40">
                      <td className="p-3 text-white font-medium">{k.keyword}</td>
                      <td className="p-3 text-right text-slate-300 font-mono">{(k.monthly_volume || 0).toLocaleString()}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                            <div className={`${diffColor(k.difficulty)} h-full`} style={{ width: `${Math.min(100, k.difficulty)}%` }}/>
                          </div>
                          <span className="text-xs text-slate-300 w-6 text-right">{k.difficulty}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right text-slate-300 font-mono">${(k.cpc_usd || 0).toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${INTENT_COLOR[k.intent] || "bg-slate-700 text-slate-300"}`}>{k.intent}</span>
                      </td>
                      <td className="p-3 text-center">
                        <TI size={15} className={`inline ${TREND_COLOR[k.trend] || "text-slate-400"}`}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
